import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Terminal, KeyRound, Workflow, Search, ScrollText, Gauge, AlertTriangle, ArrowRight } from "lucide-react";
import Layout, { PageContainer } from "../components/ui/Layout";
import { cn } from "../lib/utils";

/* ---------------------------------------------------------------- primitives */

const METHOD_STYLE: Record<string, string> = {
  GET: "bg-info/15 text-info ring-info/30",
  POST: "bg-brand/15 text-brand ring-brand/30",
};

function Method({ m }: { m: string }) {
  return (
    <span className={cn("inline-block rounded px-2 py-0.5 font-mono text-[11px] font-bold ring-1", METHOD_STYLE[m] || "bg-surface2 text-text2 ring-border")}>
      {m}
    </span>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg border border-border/70 bg-[#0d1117] px-4 py-3 text-[12.5px] leading-6 text-[#c9d1d9]">
      <code>{children}</code>
    </pre>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-muted">{label}</div>
      {children}
    </div>
  );
}

interface Param { name: string; type?: string; required?: boolean; desc: string; source?: string; }

function ParamTable({ title, rows }: { title: string; rows: Param[] }) {
  return (
    <Block label={title}>
      <div className="overflow-hidden rounded-lg border border-border/70">
        {rows.map((p, i) => (
          <div key={p.name} className={cn("px-4 py-2.5 text-[13px]", i % 2 ? "bg-surface1" : "bg-surface2/40")}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-[12.5px] font-semibold text-brand">{p.name}</span>
              {p.type && <span className="font-mono text-[11px] text-muted">{p.type}</span>}
              {p.required ? (
                <span className="rounded bg-danger/12 px-1.5 text-[10px] font-semibold text-danger ring-1 ring-danger/25">required</span>
              ) : (
                <span className="rounded bg-surface2 px-1.5 text-[10px] font-semibold text-muted ring-1 ring-border/60">optional</span>
              )}
            </div>
            <p className="mt-0.5 leading-6 text-muted">{p.desc}</p>
            {p.source && (
              <p className="mt-0.5 text-[12px] text-info">↳ where to get it: {p.source}</p>
            )}
          </div>
        ))}
      </div>
    </Block>
  );
}

interface EndpointProps {
  id: string;
  method: string;
  path: string;
  title: string;
  desc: string;
  pathParams?: Param[];
  queryParams?: Param[];
  body?: Param[];
  example: string;
  response: string;
}

function Endpoint(p: EndpointProps) {
  return (
    <div id={p.id} className="scroll-mt-6 rounded-2xl border border-border/70 bg-surface1 p-5">
      <div className="flex flex-wrap items-center gap-2.5">
        <Method m={p.method} />
        <span className="font-mono text-[14px] font-semibold text-text">{p.path}</span>
      </div>
      <p className="mt-2 text-[14px] leading-7 text-text2">{p.desc}</p>
      {p.pathParams && p.pathParams.length > 0 && <ParamTable title="Path parameters" rows={p.pathParams} />}
      {p.queryParams && p.queryParams.length > 0 && <ParamTable title="Query parameters" rows={p.queryParams} />}
      {p.body && p.body.length > 0 && <ParamTable title="Request body (JSON)" rows={p.body} />}
      <Block label="Example request"><Code>{p.example}</Code></Block>
      <Block label="Example response"><Code>{p.response}</Code></Block>
    </div>
  );
}

function GH({ id, icon: Icon, children }: { id: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <h2 id={id} className="mb-3 mt-12 flex scroll-mt-6 items-center gap-2.5 text-xl font-bold tracking-tight text-text first:mt-0">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand/12 text-brand ring-1 ring-brand/20">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      {children}
      <span aria-hidden className="ml-1 h-[3px] flex-1 rounded-full bg-gradient-to-r from-brand/40 to-transparent" />
    </h2>
  );
}

/* --------------------------------------------------------------------- data */

const TOC = [
  { id: "auth", label: "Authentication" },
  { id: "ids", label: "How the IDs connect" },
  { id: "scanning", label: "Scanning" },
  { id: "findings", label: "Findings & evidence" },
  { id: "reports", label: "Reports" },
  { id: "account", label: "Account" },
  { id: "finding-schema", label: "The finding object" },
  { id: "errors", label: "Errors & conventions" },
];

/* --------------------------------------------------------------------- page */

export default function ApiDocs() {
  const [active, setActive] = useState("auth");
  return (
    <Layout>
      <PageContainer>
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/12 text-brand ring-1 ring-brand/20">
            <Terminal className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text">API documentation</h1>
            <p className="mt-0.5 text-sm text-muted">
              Every request, its parameters, and the exact response — enough to wire Paxley into your pipeline. See the{" "}
              <Link to="/guide" className="text-brand hover:underline">product guide</Link> for the concepts.
            </p>
          </div>
        </div>

        <div className="flex gap-8">
          <nav className="sticky top-6 hidden h-fit w-52 flex-none lg:block" aria-label="API contents">
            <div className="mb-2 px-2 text-[11px] font-bold uppercase tracking-[0.14em] text-muted">On this page</div>
            <ul className="space-y-0.5">
              {TOC.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} onClick={() => setActive(s.id)}
                    className={cn("block rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors",
                      active === s.id ? "bg-brand/10 text-brand" : "text-text2 hover:bg-surface2 hover:text-text")}>
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="min-w-0 flex-1 max-w-3xl text-[15px] leading-7 text-text2">
            <p>
              The Paxley API is HTTPS + JSON. Base URL{" "}
              <span className="font-mono text-[13px] text-text">https://api.paxleysoftware.com</span>. It is the same
              API the dashboard runs on, so anything you see in the UI you can pull programmatically.
            </p>

            {/* Auth */}
            <GH id="auth" icon={KeyRound}>Authentication</GH>
            <p>
              Send your API key as the <span className="font-mono text-[13px] text-text">x-paxley-key</span> header on
              every request. There is nothing else to sign or exchange.
            </p>
            <div className="mt-3"><Code>{`x-paxley-key: pxl_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</Code></div>
            <div className="mt-3 rounded-xl border border-info/25 bg-info/5 p-4 text-[13.5px] leading-6 text-text2">
              <p><strong className="text-text">Where to get your key:</strong> your Paxley representative issues it with your workspace (it also arrives in your trial welcome email). The same key is used for every call below. It is workspace-scoped — you only ever see your own data — and can be rotated or revoked at any time. Keep it secret; treat it like a password.</p>
            </div>

            {/* IDs */}
            <GH id="ids" icon={Workflow}>How the IDs connect</GH>
            <p>
              Most calls need a <span className="font-mono text-[13px] text-text">repoId</span>,{" "}
              <span className="font-mono text-[13px] text-text">jobId</span>, or{" "}
              <span className="font-mono text-[13px] text-text">reportId</span>. You never guess these — each one is
              handed back to you by an earlier call. The whole flow:
            </p>
            <div className="mt-4 space-y-3">
              {[
                ["Start a scan", "POST /v1/scan-url (or /v1/upload)", "returns repo_id + job_id", "brand"],
                ["Watch it finish", "GET /v1/jobs/{job_id}/status", "uses the job_id from step 1", "info"],
                ["Pull the findings", "GET /v1/assurance/repos/{repo_id}", "uses the repo_id from step 1", "info"],
                ["Generate a report", "POST /v1/assurance/repos/{repo_id}/reports", "returns a report id", "brand"],
                ["Download it", "GET .../reports/{report_id}/download?format=pdf", "uses the report id from the step above", "info"],
              ].map(([step, call, note], i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-border/70 bg-surface1 p-3.5">
                  <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand/12 text-[12px] font-bold text-brand ring-1 ring-brand/20">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold text-text">{step}</div>
                    <div className="mt-0.5 font-mono text-[12px] text-text2">{call}</div>
                    <div className="mt-0.5 flex items-center gap-1 text-[12px] text-info"><ArrowRight className="h-3 w-3" />{note}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[13.5px] text-muted">
              If you already scanned in the dashboard, a repository's <span className="font-mono text-[12.5px]">repoId</span> is
              also the id in the URL of its Assurance page, and{" "}
              <span className="font-mono text-[12.5px]">GET /v1/assurance/portfolio</span> lists every repo with its id.
            </p>

            {/* Scanning */}
            <GH id="scanning" icon={Terminal}>Scanning</GH>
            <div className="space-y-5">
              <Endpoint
                id="ep-scan-url"
                method="POST"
                path="/v1/scan-url"
                title="Scan a git URL"
                desc="Clone a public git repository and run the full scan. Returns immediately with a job to poll."
                body={[
                  { name: "gitUrl", type: "string", required: true, desc: "HTTPS URL of a public git repository." },
                  { name: "projectName", type: "string", required: false, desc: "A name for the workspace entry. Re-using the same name re-scans the same repository instead of creating a new one. Defaults to the repo name." },
                ]}
                example={`curl -X POST https://api.paxleysoftware.com/v1/scan-url \\
  -H "x-paxley-key: $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "gitUrl": "https://github.com/your-org/repo",
    "projectName": "payments-service"
  }'`}
                response={`{
  "repo_id": "60e33767-ff2f-47c5-9f36-432ba3f18c9b",
  "job_id":  "abb13330-5a99-4aea-a107-2f2558167978",
  "message": "Cloning payments-service and starting scan..."
}`}
              />
              <Endpoint
                id="ep-upload"
                method="POST"
                path="/v1/upload"
                title="Scan an uploaded archive"
                desc="Upload a ZIP or TAR.GZ of your codebase (multipart/form-data) and scan it — no git remote needed."
                body={[
                  { name: "projectName", type: "form field", required: true, desc: "A name for the workspace entry." },
                  { name: "file", type: "form file", required: true, desc: "The .zip or .tar.gz archive of your source." },
                ]}
                example={`curl -X POST https://api.paxleysoftware.com/v1/upload \\
  -H "x-paxley-key: $KEY" \\
  -F "projectName=payments-service" \\
  -F "file=@./payments-service.zip"`}
                response={`{
  "repo_id": "b1c9...",
  "job_id":  "77c2...",
  "message": "Upload received for payments-service. Scan starting..."
}`}
              />
              <Endpoint
                id="ep-job-status"
                method="GET"
                path="/v1/jobs/{jobId}/status"
                title="Scan status"
                desc="Poll until status is completed (or failed). A full scan usually takes 3–4 minutes."
                pathParams={[
                  { name: "jobId", type: "uuid", required: true, desc: "The scan job to check.", source: "the job_id returned by POST /v1/scan-url or /v1/upload." },
                ]}
                example={`curl https://api.paxleysoftware.com/v1/jobs/$JOB_ID/status \\
  -H "x-paxley-key: $KEY"`}
                response={`{
  "job_id": "abb13330-5a99-4aea-a107-2f2558167978",
  "status": "completed",           // running | completed | failed
  "phase":  "complete",            // cloning | scanning | processing | complete
  "findings_count": 446,
  "message": "Scan complete. Found 446 findings.",
  "created_at": "2026-07-24T11:52:32.595Z",
  "updated_at": "2026-07-24T11:55:27.477Z"
}`}
              />
            </div>

            {/* Findings */}
            <GH id="findings" icon={Search}>Findings &amp; evidence</GH>
            <div className="space-y-5">
              <Endpoint
                id="ep-portfolio"
                method="GET"
                path="/v1/assurance/portfolio"
                title="List repositories"
                desc="Every repository in your workspace with a posture summary and its repoId — the starting point when you don't already have an id."
                example={`curl https://api.paxleysoftware.com/v1/assurance/portfolio \\
  -H "x-paxley-key: $KEY"`}
                response={`{
  "data": {
    "repositories": [
      {
        "id": "d5e1476c-10fb-40a6-b7db-7cd0bc1e8334",
        "name": "payments-service",
        "url": "manual://payments-service",
        "defaultBranch": "master",
        "lastScanAt": "2026-07-23T08:43:13.481Z",
        "lastScanStatus": "ok",
        "counts": {
          "findings": 222, "open": 222, "critical": 3,
          "high": 29, "medium": 180, "low": 9, "info": 1
        }
      }
    ]
  }
}`}
              />
              <Endpoint
                id="ep-envelope"
                method="GET"
                path="/v1/assurance/repos/{repoId}"
                title="Full evidence envelope"
                desc="The complete assessment for one repository: every finding with full evidence, plus the summary rollups, dependency & license posture, SBOM, composition, and the commit the scan is bound to. This is the endpoint to feed a pipeline or reporting engine."
                pathParams={[
                  { name: "repoId", type: "uuid", required: true, desc: "The repository to pull.", source: "the repo_id from a scan response, the id from /v1/assurance/portfolio, or the id in the Assurance page URL." },
                ]}
                example={`curl https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID \\
  -H "x-paxley-key: $KEY"`}
                response={`{
  "data": {
    "scan": { "commit": "c5cb68a7...", "status": "ok" },
    "findingsSummary": { "open": 446, "critical": 54, "high": 164, ... },
    "findings": [ /* array of finding objects — see schema below */ ],
    "dependencySummary": { ... },
    "packages": [ ... ],
    "sbom": { "format": "CycloneDX", ... },
    "licenses": { ... },
    "complianceMappings": [ ... ],
    "codeComposition": { ... },
    "developmentHealth": { ... },
    "codeSize": { /* lines of code per language — see below */ },
    "findingDensity": { /* findings normalised per KLOC — see below */ }
    /* ...one block per assurance view */
  }
}`}
              />
              <Endpoint
                id="ep-codesize"
                method="GET"
                path="/v1/assurance/repos/{repoId}"
                title="Lines of code and defect density"
                desc="Two blocks inside the evidence envelope above, for normalising finding counts by code size. codeSize counts physical lines per language, split into code, comment and blank, with vendored, minified and generated files excluded and reported. findingDensity divides the code-resident findings by those lines so you get defects per KLOC directly. Dependency and licence findings are deliberately kept out of the density numerator — they belong to imported packages, not to the lines counted — and are reported separately under excludedFromDensity."
                pathParams={[
                  { name: "repoId", type: "uuid", required: true, desc: "The repository to pull.", source: "the repo_id from a scan response, or the id from /v1/assurance/portfolio." },
                ]}
                example={`# defects per 10k lines, straight out of the envelope
curl -s https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID \
  -H "x-paxley-key: $KEY" | jq '.data.findingDensity'

# lines of code per language
curl -s https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID \
  -H "x-paxley-key: $KEY" | jq '.data.codeSize.byLanguage'`}
                response={`{
  "codeSize": {
    "status": "measured",
    "method": "Physical lines classified as code, comment or blank per file using
                language comment syntax. Vendored directories, minified bundles,
                generated files and binaries are excluded and reported below.
                Files whose extension is not recognised are counted under
                'Other' and itemised, rather than skipped.",
    "totals":               { "files": 214, "code": 18042, "comment": 2103, "blank": 2510, "total": 22655 },
    "programmingLanguages": { "files": 168, "code": 15330, "comment": 1980, "blank": 2140, "total": 19450 },
    "byLanguage": [
      { "language": "JavaScript", "kind": "programming", "files": 121,
        "code": 12004, "comment": 1502, "blank": 1610, "total": 15116 },
      { "language": "Razor",      "kind": "programming", "files": 61, "code": 2036, ... },
      { "language": "HTML",       "kind": "markup",      "files": 26, "code": 2210, ... }
    ],
    "exclusions": { "generatedOrMinified": 8, "binary": 0, "tooLarge": 0, "unreadable": 0 },
    /* Extensions the counter does not classify, with a file count each. Their
       lines are in totals under the "Other" language and are never in
       programmingLanguages. Every walked file is either counted or excluded:
       totals.files + sum(exclusions) equals the number of files seen. */
    "unrecognizedExtensions": { "cd": 1, "defaults": 1 },
    "truncated": false
  },

  "findingDensity": {
    "status": "measured",
    "codeLines": 15330,          /* programming-language code lines = the denominator */
    "totalLines": 22655,
    "findingsInScope": 46,       /* SAST + secrets + IaC + container */
    "perKloc": 3.0,
    "per10kLoc": 30.0,
    "byDomain": { "sast": 30, "secrets": 4, "iac": 12 },
    "excludedFromDensity": { "sca": 278, "license": 85 }
  }
}`}
              />
              <Endpoint
                id="ep-repos"
                method="GET"
                path="/v1/repos"
                title="Lightweight repository list"
                desc="A flat list with just the finding counts per repo — lighter than the portfolio when you only need the numbers."
                example={`curl https://api.paxleysoftware.com/v1/repos \\
  -H "x-paxley-key: $KEY"`}
                response={`{
  "repos": [
    {
      "id": "d5e1476c-...",
      "url": "manual://payments-service",
      "default_branch": "master",
      "open_findings": 222, "open_critical": 3, "open_high": 29,
      "fixed_count": 0, "last_scan_at": "2026-07-23T06:55:22Z",
      "last_scan_status": "ok"
    }
  ]
}`}
              />
            </div>

            {/* Reports */}
            <GH id="reports" icon={ScrollText}>Reports</GH>
            <div className="space-y-5">
              <Endpoint
                id="ep-report-create"
                method="POST"
                path="/v1/assurance/repos/{repoId}/reports"
                title="Generate a report"
                desc="Start report generation from the latest completed scan. Returns a report record with an id; generation is asynchronous, so poll its status before downloading."
                pathParams={[
                  { name: "repoId", type: "uuid", required: true, desc: "The repository to report on.", source: "same repoId as above." },
                ]}
                body={[
                  { name: "reportType", type: "string", required: true, desc: "One of: comprehensive, executive, technical, exploitability, architecture, supply-chain, compliance, remediation, findings." },
                  { name: "formats", type: "string[]", required: true, desc: "Any of: pdf, html, json, csv, xml, sarif, cyclonedx, spdx." },
                  { name: "options", type: "object", required: false, desc: "Optional: preparedFor, clientName, classification, severityThreshold, and include* section toggles." },
                ]}
                example={`curl -X POST https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID/reports \\
  -H "x-paxley-key: $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "reportType": "technical",
    "formats": ["pdf", "sarif", "xml"],
    "options": { "preparedFor": "Acme Security", "classification": "Confidential" }
  }'`}
                response={`{
  "report": {
    "id": "126873dd-485a-49db-bb7c-19728d847460",
    "repoId": "60e33767-...",
    "commit": "c5cb68a7...",
    "reportType": "technical",
    "formats": ["pdf", "sarif", "xml"],
    "status": "queued",           // queued | generating | completed | failed
    "immutable": true
  }
}`}
              />
              <Endpoint
                id="ep-report-status"
                method="GET"
                path="/v1/assurance/repos/{repoId}/reports/{reportId}/status"
                title="Report status"
                desc="Poll until status is completed, then download."
                pathParams={[
                  { name: "repoId", type: "uuid", required: true, desc: "The repository.", source: "same repoId." },
                  { name: "reportId", type: "uuid", required: true, desc: "The report to check.", source: "the report.id returned by the generate call." },
                ]}
                example={`curl https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID/reports/$REPORT_ID/status \\
  -H "x-paxley-key: $KEY"`}
                response={`{ "status": "completed", "progressPercent": 100 }`}
              />
              <Endpoint
                id="ep-report-download"
                method="GET"
                path="/v1/assurance/repos/{repoId}/reports/{reportId}/download"
                title="Download an artifact"
                desc="Download one generated format. Only formats requested at generation time are available."
                pathParams={[
                  { name: "repoId", type: "uuid", required: true, desc: "The repository.", source: "same repoId." },
                  { name: "reportId", type: "uuid", required: true, desc: "The completed report.", source: "the report.id from the generate call." },
                ]}
                queryParams={[
                  { name: "format", type: "string", required: true, desc: "Which artifact to download: pdf, html, json, csv, xml, sarif, cyclonedx, or spdx." },
                ]}
                example={`curl -OJ \\
  "https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID/reports/$REPORT_ID/download?format=sarif" \\
  -H "x-paxley-key: $KEY"`}
                response={`# the raw file bytes, with:
#   Content-Type: application/sarif+json
#   Content-Disposition: attachment; filename="paxley-technical-....sarif"
#   x-content-sha256: <integrity hash>`}
              />
              <Endpoint
                id="ep-report-share"
                method="POST"
                path="/v1/assurance/repos/{repoId}/reports/{reportId}/share"
                title="Create a share link"
                desc="Mint a public, read-only link to a completed report that anyone can open without a login. Valid 30 days and revocable."
                pathParams={[
                  { name: "repoId", type: "uuid", required: true, desc: "The repository.", source: "same repoId." },
                  { name: "reportId", type: "uuid", required: true, desc: "The completed report.", source: "the report.id." },
                ]}
                example={`curl -X POST https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID/reports/$REPORT_ID/share \\
  -H "x-paxley-key: $KEY"`}
                response={`{
  "url": "https://api.paxleysoftware.com/share/reports/<token>",
  "expiresAt": "2026-08-23T07:01:42.197Z"
}`}
              />
            </div>

            {/* Account */}
            <GH id="account" icon={Gauge}>Account</GH>
            <Endpoint
              id="ep-usage"
              method="GET"
              path="/v1/usage"
              title="Usage & allowance"
              desc="Your scan and repository allowance and how much is used. Returns { limits: null } for unmetered workspaces."
              example={`curl https://api.paxleysoftware.com/v1/usage \\
  -H "x-paxley-key: $KEY"`}
              response={`{
  "limits": {
    "scansUsed": 1, "maxScans": 5,
    "reposUsed": 2, "maxRepos": 5
  }
}`}
            />

            {/* Finding schema */}
            <GH id="finding-schema" icon={Search}>The finding object</GH>
            <p>Every object in <span className="font-mono text-[13px] text-text">data.findings[]</span> has this shape. Fields that were not measured are <span className="font-mono text-[13px] text-text">null</span> rather than guessed.</p>
            <div className="mt-3"><Code>{`{
  "id": "1a252ed2-0922-473c-aff4-41875664463e",
  "title": "Code execution depends on a user-provided value",
  "severity": "critical",                 // critical | high | medium | low | info
  "status": "open",                       // open | fixed | suppressed
  "domain": "sast",                       // sast | sca | secret | iac | license
  "capability": "Source-code security analysis",
  "location": { "file": "app/routes/contributions.js", "line": 34,
                "endLine": 34, "startColumn": null, "endColumn": null },

  // --- code findings (SAST) ---
  "cwe": "CWE-094",
  "cweName": "CWE-94: Code Injection",
  "codeSnippet": "eval(req.body.roth)",
  "dataflow": [                           // ordered source -> sink
    { "file": "app/routes/contributions.js", "line": 34, "message": "req.body" },
    { "file": "app/routes/contributions.js", "line": 34, "message": "req.body.roth" }
  ],
  "confidence": "high",                   // high | medium | low (when reported)
  "corroboratedBy": ["semantic dataflow analysis", "pattern-based static analysis"],

  // --- dependency findings (SCA) ---
  "cve": "CVE-2026-54466",
  "cvssScore": 7.5, "cvssVector": "CVSS:3.1/AV:N/...",
  "package": "websocket-driver", "version": "0.7.0",
  "fixedVersion": "0.7.5",
  "upgradeJump": "patch",                 // patch | minor | major
  "upgradeEffort": "low",                 // low | medium | high
  "usageSites": 3,                        // source files importing the package

  // --- always present ---
  "recommendation": "Never pass untrusted input to eval / new Function ...",
  "references": ["https://osv.dev/vulnerability/GHSA-xv26-6w52-cph6",
                 "https://nvd.nist.gov/vuln/detail/CVE-2026-54466"],
  "kev": false, "epss": 0.12, "reachability": "not_measured",
  "ranking": { "value": 812, "scale": 1000, "source": "stored_priority_score" },
  "evidenceChain": [ { "step": "...", "detail": "..." } ],
  "validationSteps": [ "Reproduce ...", "Apply the fix ...", "Re-scan ..." ]
}`}</Code></div>

            {/* Errors */}
            <GH id="errors" icon={AlertTriangle}>Errors &amp; conventions</GH>
            <p>Errors return a JSON body with a conventional HTTP status code:</p>
            <div className="mt-3"><Code>{`{ "error": "scan_limit_reached", "message": "Your trial includes 5 scans ..." }`}</Code></div>
            <div className="mt-3 overflow-hidden rounded-lg border border-border/70">
              {[
                ["200 / 202", "Success. 202 means accepted and processing (a scan or report was queued)."],
                ["401", "Missing or invalid API key."],
                ["403", "Not allowed — or your scan / repository allowance is used up."],
                ["404", "The repository, job, or report id does not exist in your workspace."],
                ["400", "Malformed request — check the body fields and the format value."],
              ].map(([code, desc], i) => (
                <div key={code} className={cn("grid grid-cols-[80px_1fr] gap-3 px-4 py-2.5 text-[13px]", i % 2 ? "bg-surface1" : "bg-surface2/40")}>
                  <span className="font-mono text-[12.5px] font-semibold text-text">{code}</span>
                  <span className="text-muted">{desc}</span>
                </div>
              ))}
            </div>
            <ul className="mt-4 space-y-1.5 text-[14px]">
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span>Re-using a <span className="font-mono text-[12px]">projectName</span> re-scans the same repository (a new scan, not a duplicate repo).</span></li>
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span>Scanned source is discarded after analysis; only findings and evidence are retained.</span></li>
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span>Every report is bound to an immutable, commit-scoped snapshot and carries a SHA-256 integrity hash.</span></li>
            </ul>
          </div>
        </div>
      </PageContainer>
    </Layout>
  );
}
